export type PasswordPolicyRule = 'minLength' | 'requiredClasses';

type PasswordPolicyRules = Record<PasswordPolicyRule, number>;

type Bounds = { min: number; max: number };

export type PasswordPolicyOverride = Partial<Record<PasswordPolicyRule, number | null>>;

export type PasswordPolicySettings = PasswordPolicyRules & {
  id: string;
  override: PasswordPolicyOverride;
  default: PasswordPolicyRules;
  bounds: Record<PasswordPolicyRule, Bounds>;
};

export const PASSWORD_POLICY_RULES: PasswordPolicyRule[] = ['minLength', 'requiredClasses'];

export const PASSWORD_POLICY_RULE_LABELS: Record<PasswordPolicyRule, string> = {
  minLength: 'Minimum length',
  requiredClasses: 'Required character types',
};

export const PASSWORD_POLICY_FORM_DEFAULTS = {
  override: { minLength: null, requiredClasses: null },
};

export const PASSWORD_POLICY_APPLIES_FROM_NOW_ON = 'Applies to passwords set from now on. Existing '
  + 'passwords stay valid until each Member next resets theirs.';

const describeRules = ({ minLength, requiredClasses }: PasswordPolicyRules) => (
  `${minLength} characters and ${requiredClasses} character types`
);

export const weakerThanDefaultWarning = (defaults: PasswordPolicyRules) => (
  `Weaker than Pingwire's recommended policy (${describeRules(defaults)}).`
);

export const resetToDefaultQuestion = (defaults: PasswordPolicyRules) => (
  `Follow Pingwire's default policy, currently ${describeRules(defaults)}?`
);

export const isCustomised = (
  override: PasswordPolicyOverride | undefined,
  rule: PasswordPolicyRule,
) => (
  override?.[rule] !== undefined && override[rule] !== null
);

export const buildPasswordPolicyUpdate = ({ override }: { override: PasswordPolicyOverride }) => (
  Object.fromEntries(PASSWORD_POLICY_RULES
    .filter((rule) => isCustomised(override, rule))
    .map((rule) => [rule, override[rule]]))
);

export const isWeakerThanDefault = (
  override: PasswordPolicyOverride | undefined,
  defaults: PasswordPolicyRules,
) => PASSWORD_POLICY_RULES.some((rule) => (override?.[rule] ?? defaults[rule]) < defaults[rule]);

export const hasOverride = (settings: PasswordPolicySettings) => (
  PASSWORD_POLICY_RULES.some((rule) => isCustomised(settings.override, rule))
);

export const isWithinBounds = (value: number, { min, max }: Bounds) => (
  Number.isInteger(value) && value >= min && value <= max
);

export const boundsRange = ({ min, max }: Bounds) => (
  Array.from({ length: max - min + 1 }, (_, index) => min + index)
);

export const outOfBoundsMessage = ({ min, max }: Bounds) => `Must be between ${min} and ${max}`;

export const validateWithinBounds = (bounds: Bounds) => (value?: number | null) => (
  value === undefined || value === null || isWithinBounds(value, bounds)
    ? undefined
    : outOfBoundsMessage(bounds)
);
